import {
    TokenAmount,
    TransactionDetailRow,
    TransactionFeeSection,
    TransactionDialogContent,
    TransactionSection,
} from "@/components";
import { useRedeemTransaction } from "@/hooks";
import { TOKEN_IDS, type TokenId } from "@/types";
import { Divider } from "@/components";
import { useEffect } from "react";
import { useApprovalStore } from "@/zustand/ApprovalStore";
import { useTokenApprovalV2 } from "@/hooks/transaction/useTokenApprovalV2";
import { getGatewayContractAddress } from "@/config";
import { mainnet } from "viem/chains";
import { ActionText } from "@/constants/text.enum";
import { getTransactionLoadingState } from "./transaction-loading.utils";

export interface RedeemTransactionProps {
    redeemAmount: number;
    redeemTokenId: TokenId;
    onClose?: () => void;
}

export function RedeemTransaction({ redeemAmount, redeemTokenId, onClose }: RedeemTransactionProps) {
    const { setAmount, reset } = useApprovalStore();

    // Reset approval state, and set amount once
    useEffect(() => {
        setAmount(redeemAmount);
        return () => reset();
    }, [redeemAmount, setAmount, reset]);

    const {
        gasPriceUSD,
        executeRedeemTransaction,
        isLoading: isRedeemTransactionLoading,
        isExecuting,
    } = useRedeemTransaction({
        redeemAmount,
        redeemTokenId,
    });

    const { isApprovalLoading, withApproval, approvalLoadingText } = useTokenApprovalV2({
        tokenId: redeemTokenId,
        amount: redeemAmount,
        spender: getGatewayContractAddress(mainnet.id),
    });

    const REDEEM_TEXT = <TokenAmount amount={redeemAmount} tokenId={TOKEN_IDS.IUSD} size="md" />;
    const RECEIVE_TEXT = <TokenAmount amount={redeemAmount} tokenId={TOKEN_IDS.USDC} size="md" />;
    const CONFIRM_TEXT = "Confirm Redeem";

    const { isLoading, loadingText } = getTransactionLoadingState({
        approvalLoadingText,
        isApprovalLoading,
        isExecuting,
        actionLoadingText: ActionText.REDEEMING,
        isTransactionLoading: isRedeemTransactionLoading,
    });
    const isDisabled = isLoading || !redeemAmount || !redeemTokenId;

    const handleConfirm = async () => {
        try {
            const hasExecuted = await withApproval(
                async () => {
                    await executeRedeemTransaction();
                    return true;
                },
                { isExecuting, amount: redeemAmount },
            );

            if (!hasExecuted) return;

            onClose?.();
        } catch (error) {
            console.error("Error executing redeem transaction:", error);
        }
    };

    return (
        <TransactionDialogContent
            onClose={onClose}
            onConfirm={handleConfirm}
            isLoading={isLoading}
            confirmText={CONFIRM_TEXT}
            loadingText={loadingText}
            disabled={isDisabled}
            className="w-[420px]"
        >
            <TransactionSection>
                <TransactionDetailRow label="Redeem" value={REDEEM_TEXT} />
                <TransactionDetailRow label="Receive" value={RECEIVE_TEXT} />
            </TransactionSection>
            <Divider />
            <TransactionFeeSection gasPriceUSD={gasPriceUSD} />
        </TransactionDialogContent>
    );
}
